import { useEffect, useState } from 'react'
import { collection, getCountFromServer, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'

/**
 * The last 30 days of per-block counts, per flow — 13 Oct 2026.
 *
 * Read from `skidaCrmAutomationStats/{flow}__{day}`, which the engine and the
 * delivery webhook write (see `functions-webhook/src/automation/stats.ts`). A
 * flow with no document in the window has never run in it, and its counts show
 * "–" rather than 0.
 */
export type FlowCounts = Record<string, Record<string, number>>

export const PERIOD_LABEL = 'Counts: last 30 days'

export function useAutomationStats(onError: (e: Error) => void): FlowCounts {
  const [counts, setCounts] = useState<FlowCounts>({})
  useEffect(() => {
    const start = new Date(Date.now() - 29 * 24 * 3600_000).toISOString().slice(0, 10)
    return onSnapshot(
      query(collection(db, 'skidaCrmAutomationStats'), where('day', '>=', start)),
      (snap) => {
        const out: FlowCounts = {}
        for (const d of snap.docs) {
          const flow = String(d.get('flow') ?? '')
          const c = (d.get('counts') ?? {}) as Record<string, number>
          const sum = (out[flow] ??= {})
          for (const [k, v] of Object.entries(c)) sum[k] = (sum[k] ?? 0) + (typeof v === 'number' ? v : 0)
        }
        setCounts(out)
      },
      (e) => onError(e),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return counts
}

/**
 * How many runs of a flow are waiting at each block right now, keyed by the
 * block they will wake at. Counted on the server; nothing is downloaded.
 */
export function useWaitingNow(flow: string, wakeAt: { at: string; status: 'waiting' | 'batch' }[]) {
  const [out, setOut] = useState<Record<string, number>>({})
  const key = wakeAt.map((w) => `${w.status}:${w.at}`).join(',')
  useEffect(() => {
    let live = true
    Promise.all(
      wakeAt.map(async (w) => {
        const snap = await getCountFromServer(
          query(
            collection(db, 'skidaCrmAutomationRuns'),
            where('flow', '==', flow),
            where('status', '==', w.status),
            where('at', '==', w.at),
          ),
        )
        return [`${w.status}:${w.at}`, snap.data().count] as const
      }),
    )
      .then((rows) => live && setOut(Object.fromEntries(rows)))
      // A count that cannot be read stays "–"; it is not worth a banner.
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, key])
  return out
}

export interface QueueState {
  id: string
  season: string
  hemisphere: string
  total: number
  next: number
}

/** The daily-batch queues of one flow — `skidaCrmAutomationQueues`. */
export function useQueues(flow: string): QueueState[] {
  const [rows, setRows] = useState<QueueState[]>([])
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'skidaCrmAutomationQueues'), where('flow', '==', flow)),
        (snap) =>
          setRows(
            snap.docs.map((d) => ({
              id: d.id,
              season: d.id.split('__')[1] ?? '',
              hemisphere: String(d.get('hemisphere') ?? ''),
              total: Number(d.get('total') ?? 0),
              next: Number(d.get('next') ?? 0),
            })),
          ),
        // Unreadable means nothing to show; the panel says the queue is not built.
        () => setRows([]),
      ),
    [flow],
  )
  return rows
}
